<a href="#main-content" class="visually-hidden-focusable">Skip to main content</a>

<div class="dashboard-content">
    <h1 class="visually-hidden">Service Request</h1>

    <div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true" #liveRegion></div>

    <div class="container-fluid mt-4">
        <div class="row" id="main-content">
            <div class="col-xxl-1"></div>
            <div class="col-xxl-10">
                <div class="row g-4 mb-3">
                    <div class="col-12">
                        <div class="card pb-3">
                            <div class="card-header">
                                <div class="d-flex align-items-center">
                                    <i class="fa-solid fa-hand-holding-hand card-title-icon me-2"
                                        aria-hidden="true"></i>
                                    <div class="dashboard-card-title">Create a Service Request</div>
                                </div>
                                <div class="dashboard-card-subtitle">
                                    We're here to help! Answer a few questions and we'll connect you with the right
                                    resources.
                                    <strong>You don't need an existing case to get started.</strong>
                                </div>
                            </div>
                            <div class="card-body px-0 px-md-3">
                                <mdb-stepper #stepper [linear]="true" [orientation]="stepperOrientation()"
                                    [mobile]="isMobile()">

                                    <!-- STEP 1: Case Type -->
                                    <mdb-step [stepForm]="caseTypeForm" [name]="'Case Type'"
                                        [editable]="!isSubmitted()">
                                        <form [formGroup]="caseTypeForm" class="mt-3">
                                            <p class="mb-4">
                                                Select the category that best describes your situation. If you are not
                                                sure, choose "I don't know."
                                            </p>

                                            <div class="row">
                                                <div class="col-12 col-md-2 pb-md-3">
                                                    <mdb-form-control>
                                                        <mdb-select formControlName="caseType"
                                                            aria-labelledby="caseTypeLabel"
                                                            aria-label="Case Type, required" aria-required="true"
                                                            [attr.aria-invalid]="caseType.invalid && (caseType.dirty || caseType.touched)">
                                                            <mdb-option [value]="'family-law'">Family Law</mdb-option>
                                                            <mdb-option [value]="'child-support'">Child
                                                                Support</mdb-option>
                                                            <mdb-option
                                                                [value]="'landLord-tenant'">Landlord/Tenant</mdb-option>
                                                            <mdb-option [value]="'small-claims'">Small
                                                                Claims</mdb-option>
                                                            <mdb-option [value]="'care'">CARE</mdb-option>
                                                            <mdb-option [value]="'unknown'">I don't know</mdb-option>
                                                        </mdb-select>
                                                        <label mdbLabel class="form-label" id="caseTypeLabel">
                                                            Case Type <span class="form-required-asterisk"
                                                                aria-hidden="true">*</span>
                                                        </label>
                                                        @if (caseType.invalid && (caseType.dirty || caseType.touched)) {
                                                        <mdb-error class="mt-n1">Please select a case type to
                                                            continue.</mdb-error>
                                                        }
                                                        @if (caseType.valid) {
                                                        <mdb-success></mdb-success>
                                                        }
                                                    </mdb-form-control>
                                                </div>
                                            </div>
                                        </form>
                                        <!-- Navigation Buttons -->
                                        @if(!isMobile())
                                        {
                                        <div class="row pt-3">
                                            <div class="col-12 col-md-2 col-lg-2">
                                                <div class="row">
                                                    <div class="col-12 col-md-12">
                                                        <button class="btn btn-md btn-primary-stepper w-100 px-2 py-2"
                                                            (click)="nextStep(stepper, caseTypeForm)">
                                                            Next<i class="fa-solid fa-arrow-right ms-2"
                                                                aria-hidden="true"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                    </mdb-step>

                                    <!-- STEP 2: Related Case (Optional) -->
                                    <mdb-step [stepForm]="relatedCaseForm" [name]="'Link a Case'" [optional]="true"
                                        [editable]="!isSubmitted()">
                                        <form [formGroup]="relatedCaseForm" class="mt-3">
                                            <p class="mb-4">
                                                What is your case number (if you have one)?
                                            </p>
                                            <div class="row align-items-center  pb-md-3">
                                                <div class="col-12 col-md-3 align-items-center">
                                                    <mdb-form-control>
                                                        <input mdbValidate mdbInput type="text" id="relatedCase"
                                                            formControlName="relatedCase" class="form-control"
                                                            [maxlength]="200" />
                                                        <label mdbLabel class="form-label" for="relatedCase">
                                                            Case Number
                                                        </label>
                                                    </mdb-form-control>
                                                </div>
                                            </div>
                                        </form>
                                        <!-- Navigation Buttons -->
                                        @if(!isMobile())
                                        {
                                        <div class="row pt-3">
                                            <div class="col-12 col-md-4 col-lg-4">
                                                <div class="d-flex justify-content-center gap-2">
                                                    <button
                                                        class="btn btn-md btn-outline-primary-stepper w-100 px-2 py-2"
                                                        (click)="stepper.previous()">
                                                        <i class="fa-solid fa-arrow-left me-2"
                                                            aria-hidden="true"></i>Previous
                                                    </button>
                                                    <button class="btn btn-md btn-primary-stepper w-100 px-2 py-2"
                                                        (click)="nextStep(stepper, relatedCaseForm)">
                                                        Next<i class="fa-solid fa-arrow-right ms-2"
                                                            aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                    </mdb-step>

                                    <!-- STEP 3: Service Category & Details -->
                                    <mdb-step [stepForm]="serviceCategoryForm" [name]="'Service Details'"
                                        [editable]="!isSubmitted()">
                                        <form [formGroup]="serviceCategoryForm" class="mt-3">
                                            <p class="mb-4">
                                                Select the type of assistance you need, then please provide details
                                                about your request.
                                            </p>

                                            <!-- Category Selection with Conditional Fields -->
                                            <div class="row pb-md-3">
                                                <!-- Radio buttons column -->
                                                <div class="col-12 col-lg-3 mb-3 mb-lg-0">
                                                    <label class="form-label fw-bold mb-3">
                                                        Choose a service <span class="form-required-asterisk"
                                                            aria-hidden="true">*</span>
                                                    </label>

                                                    @if (canSelectDocumentPreparation) {
                                                    <div class="form-check mb-2">
                                                        <input mdbRadio class="form-check-input" type="radio"
                                                            id="categoryDocPrep" formControlName="selectedCategory"
                                                            value="docPrep" />
                                                        <label class="form-check-label" for="categoryDocPrep">
                                                            Document Preparation
                                                        </label>
                                                    </div>
                                                    }

                                                    <div class="form-check mb-2">
                                                        <input mdbRadio class="form-check-input" type="radio"
                                                            id="categoryFormReview" formControlName="selectedCategory"
                                                            value="formReview" />
                                                        <label class="form-check-label" for="categoryFormReview">
                                                            Form Review
                                                        </label>
                                                    </div>

                                                    <div class="form-check mb-2">
                                                        <input mdbRadio class="form-check-input" type="radio"
                                                            id="categoryCommunication"
                                                            formControlName="selectedCategory" value="communication" />
                                                        <label class="form-check-label" for="categoryCommunication">
                                                            Account Issue / Verification
                                                        </label>
                                                    </div>

                                                    <div class="form-check">
                                                        <input mdbRadio class="form-check-input" type="radio"
                                                            id="categoryQuestion" formControlName="selectedCategory"
                                                            value="question" />
                                                        <label class="form-check-label" for="categoryQuestion">
                                                            Ask a Question
                                                        </label>
                                                    </div>
                                                </div>

                                                <!-- Conditional Fields Column (Desktop: Right side, Mobile: Below) -->
                                                <div class="col-12 col-lg-9">
                                                    <!-- Document Preparation Fields -->
                                                    @if (selectedCategory.value === 'docPrep') {
                                                    <div class="ps-lg-3">
                                                        <h5 class="mb-3">Document Preparation Details</h5>
                                                        <div class="row">
                                                            <div class="col-12 col-md-3">
                                                                <mdb-form-control class="mb-3">
                                                                    <mdb-select formControlName="docPrepFormType"
                                                                        aria-labelledby="formTypeLabel"
                                                                        [clearButton]="true">
                                                                        <mdb-option
                                                                            [value]="'petition'">Start Case</mdb-option>
                                                                        <mdb-option
                                                                            [value]="'response'">Respond to Case</mdb-option>
                                                                        <mdb-option [value]="'order'">Request an Order</mdb-option>
                                                                        <mdb-option [value]="'other'">Other</mdb-option>
                                                                    </mdb-select>
                                                                    <label mdbLabel class="form-label"
                                                                        id="formTypeLabel">Type of Form
                                                                        <span class="form-required-asterisk"
                                                                            aria-hidden="true">*</span></label>
                                                                    @if (docPrepFormType.invalid &&
                                                                    (docPrepFormType.dirty || docPrepFormType.touched))
                                                                    {
                                                                    <mdb-error class="mt-n1">Please select a form
                                                                        type.</mdb-error>
                                                                    }
                                                                    @if (docPrepFormType.valid) {
                                                                    <mdb-success></mdb-success>
                                                                    }
                                                                </mdb-form-control>
                                                            </div>
                                                        </div>

                                                    </div>
                                                    }

                                                    <!-- Form Review Fields -->
                                                    @if (selectedCategory.value === 'formReview') {
                                                    <div class="ps-lg-3">
                                                        <h5 class="mb-3">Form Review Details</h5>
                                                        <div class="row">
                                                            <div class="col-12 col-md-3">
                                                                <mdb-form-control class="mb-3">
                                                                    <mdb-select formControlName="formReviewType"
                                                                        [clearButton]="true">
                                                                        <mdb-option
                                                                            [value]="'petition'">Start Case</mdb-option>
                                                                        <mdb-option
                                                                            [value]="'response'">Respond to Case</mdb-option>
                                                                        <mdb-option [value]="'order'">Request an Order</mdb-option>
                                                                        <mdb-option [value]="'other'">Other</mdb-option>
                                                                    </mdb-select>
                                                                    <label mdbLabel class="form-label">Type of Form
                                                                        <span class="form-required-asterisk"
                                                                            aria-hidden="true">*</span></label>
                                                                    @if (formReviewType.invalid && (formReviewType.dirty
                                                                    || formReviewType.touched)) {
                                                                    <mdb-error class="mt-n1">Please select a form
                                                                        type.</mdb-error>
                                                                    }
                                                                    @if (formReviewType.valid) {
                                                                    <mdb-success></mdb-success>
                                                                    }
                                                                </mdb-form-control>
                                                            </div>
                                                        </div>

                                                    </div>
                                                    }
                                                </div>
                                            </div>
                                        </form>
                                        <!-- Navigation Buttons -->
                                        @if(!isMobile())
                                        {
                                        <div class="row pt-3">
                                            <div class="col-12 col-md-4 col-lg-4">
                                                <div class="d-flex justify-content-center gap-2">
                                                    <button
                                                        class="btn btn-md btn-outline-primary-stepper w-100 px-2 py-2"
                                                        (click)="stepper.previous()">
                                                        <i class="fa-solid fa-arrow-left me-2"
                                                            aria-hidden="true"></i>Previous
                                                    </button>
                                                    <button class="btn btn-md btn-primary-stepper w-100 px-2 py-2"
                                                        (click)="nextStep(stepper, serviceCategoryForm)">
                                                        Next<i class="fa-solid fa-arrow-right ms-2"
                                                            aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                    </mdb-step>

                                    <!-- STEP 4: Additional Details -->
                                    <mdb-step [stepForm]="serviceDescriptionForm" [name]="'Additional Details'"
                                        [editable]="!isSubmitted()">
                                        <form [formGroup]="serviceDescriptionForm">
                                            <!-- Description (Always shown) -->
                                            <p class="mb-4">
                                                Please provide any additional details
                                                about your request.
                                            </p>
                                            <div class="row">
                                                <div class="col-12 col-md-6 col-lg-8">
                                                    <mdb-form-control>
                                                        <textarea mdbValidate mdbInput id="description"
                                                            formControlName="description" class="form-control mb-4"
                                                            rows="5" minlength="8" maxlength="200"
                                                            placeholder="Please describe what you need help with..."></textarea>
                                                        <label mdbLabel class="form-label" for="description">
                                                            Description <span class="form-required-asterisk"
                                                                aria-hidden="true">*</span>
                                                        </label>
                                                        @if (description.invalid && (description.dirty ||
                                                        description.touched))
                                                        {
                                                        <mdb-error class="mt-n1">Please describe your
                                                            request.</mdb-error>
                                                        }
                                                        @if (description.valid) {
                                                        <mdb-success></mdb-success>
                                                        }
                                                    </mdb-form-control>
                                                </div>
                                            </div>
                                        </form>
                                        <!-- Navigation Buttons -->
                                        @if(!isMobile())
                                        {
                                        <div class="row pt-3">
                                            <div class="col-12 col-md-4 col-lg-4">
                                                <div class="d-flex justify-content-center gap-2">
                                                    <button
                                                        class="btn btn-md btn-outline-primary-stepper w-100 px-2 py-2"
                                                        (click)="stepper.previous()">
                                                        <i class="fa-solid fa-arrow-left me-2"
                                                            aria-hidden="true"></i>Previous
                                                    </button>
                                                    <button class="btn btn-md btn-primary-stepper w-100 px-2 py-2"
                                                        (click)="nextStep(stepper, serviceDescriptionForm)">
                                                        Next<i class="fa-solid fa-arrow-right ms-2"
                                                            aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                    </mdb-step>

                                    <!-- STEP 5: Attachments (Optional) -->
                                    <mdb-step [stepForm]="attachmentsForm" [name]="'Additional Documents (Optional)'"
                                        [editable]="!isSubmitted()">
                                        <form [formGroup]="attachmentsForm" class="mt-3">
                                            <p class="mb-4">
                                                Upload any documents that might help the team understand your request
                                                better.
                                                This step is optional.
                                            </p>

                                            <div class="row">
                                                <div class="col-12 col-md-6">
                                                    <!-- File Upload Area -->
                                                    <div class="file-upload-outline">
                                                        <mdb-file-upload #fileUpload [multiple]="true"
                                                            [maxFileQuantity]="maxNumberFiles"
                                                            [maxFileSize]="maxSingleFileSize"
                                                            [acceptedExtensions]="'.pdf,.jpg,.jpeg,.png,.docx'"
                                                            [defaultMsg]="'Drag and drop files or click to browse'"
                                                            [removeBtnDisabled]="true" [mainError]="'File upload error'"
                                                            [height]="200" (fileAdded)="onFileAdded($event)"
                                                            (fileRemoved)="onFileRemoved($event)"
                                                            aria-label="File upload drag and drop area">
                                                        </mdb-file-upload>
                                                    </div>
                                                </div>
                                                <div class="row">
                                                    <div class="d-block mt-2 fs-5">
                                                        <i class="fa-solid fa-circle-info me-2" aria-hidden="true"></i>
                                                        Accepted: PDF, JPG, PNG, DOCX | Max {{ maxSingleFileSize }}MB
                                                        per file |
                                                        Max {{ maxTotalFileSize }}MB total
                                                    </div>
                                                </div>
                                            </div>


                                            <!-- Uploaded Files List -->
                                            @if (uploadedFiles().length > 0) {
                                            <div class="uploaded-files-list mt-4" role="region"
                                                aria-labelledby="uploaded-files-heading">
                                                <h6 class="mb-3" id="uploaded-files-heading">
                                                    Uploaded Files ({{ uploadedFiles().length }})
                                                </h6>

                                                <!-- Storage Capacity Progress Bar -->
                                                <div class="mt-3 mb-3">
                                                    <div class="row">
                                                        <div class="col-12 col-md-6">
                                                            <div
                                                                class="d-flex justify-content-between align-items-center mb-2">
                                                                <small class="text-muted">
                                                                    Total File Size: {{
                                                                    formatFileSize(getTotalFileSizeBytes())
                                                                    }} / {{ maxTotalFileSize }}MB
                                                                </small>
                                                                <small class="fw-semibold"
                                                                    [class.text-danger]="uploadedFilesTotalPercentage() > 90"
                                                                    [class.text-warning]="uploadedFilesTotalPercentage() > 75 && uploadedFilesTotalPercentage() <= 90"
                                                                    [class.text-success]="uploadedFilesTotalPercentage() <= 75">
                                                                    {{ uploadedFilesTotalPercentage() }}% of limit used
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="row">
                                                        <div class="col-12 col-md-6">
                                                            <div class="progress" style="height: 20px;">
                                                                <div class="progress-bar"
                                                                    [class.bg-success]="uploadedFilesTotalPercentage() <= 75"
                                                                    [class.bg-warning]="uploadedFilesTotalPercentage() > 75 && uploadedFilesTotalPercentage() <= 90"
                                                                    [class.bg-danger]="uploadedFilesTotalPercentage() > 90"
                                                                    role="progressbar"
                                                                    [style.width.%]="getCappedUploadPercentage()"
                                                                    [attr.aria-valuenow]="getCappedUploadPercentage()"
                                                                    [attr.aria-label]="'Storage usage: ' + uploadedFilesTotalPercentage() + ' percent of ' + maxTotalFileSize + ' megabytes'"
                                                                    aria-valuemin="0" aria-valuemax="100">
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>

                                                @if (isTotalFileSizeExceeded()) {
                                                <div class="alert alert-danger col-12 col-md-6" role="alert"
                                                    aria-live="assertive">
                                                    <i class="fa-solid fa-circle-exclamation me-2"
                                                        aria-hidden="true"></i>
                                                    <strong>Total upload limit exceeded.</strong>
                                                    Your selected files total
                                                    {{ formatFileSize(getTotalFileSizeBytes()) }}. The maximum combined
                                                    size is {{ maxTotalFileSize }}MB. Remove one or more files before
                                                    continuing.
                                                </div>
                                                }

                                                <!-- File List -->
                                                <div class="row">
                                                    <div class="col-12 col-md-6">
                                                        <ul class="list-group" aria-labelledby="uploaded-files-heading">
                                                            @for (file of uploadedFiles(); track file.name + file.size)
                                                            {
                                                            <li class="list-group-item py-2">
                                                                <div class="d-flex align-items-center gap-2">
                                                                    <!-- Icon (hidden on mobile) -->
                                                                    <div class="file-icon d-none d-md-flex"
                                                                        aria-hidden="true">
                                                                        <i class="fas fa-file text-muted fs-4"></i>
                                                                    </div>

                                                                    <!-- Filename - takes all remaining left space -->
                                                                    <span
                                                                        class="fw-semibold text-truncate flex-grow-1 min-w-0"
                                                                        [title]="file.name">
                                                                        {{ file.name }}
                                                                    </span>

                                                                    <!-- Right side: type, size, remove - all pinned right -->
                                                                    <div
                                                                        class="d-flex align-items-center gap-2 flex-shrink-0">
                                                                        <span class="badge file-badge">{{
                                                                            getFileExtension(file) }}</span>
                                                                        <span class="small text-muted text-nowrap">{{
                                                                            formatFileSize(file.size) }}</span>
                                                                        <button type="button"
                                                                            class="btn btn-sm btn-link p-1"
                                                                            (click)="removeFile(file)"
                                                                            [attr.aria-label]="'Remove ' + file.name">
                                                                            <i class="fas fa-times fs-4"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </li>
                                                            }
                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>
                                            }
                                        </form>
                                        <!-- Navigation Buttons -->
                                        @if (!isMobile()) {
                                        <div class="row pt-3">
                                            <div class="col-12 col-md-4 col-lg-4">
                                                <div class="d-flex justify-content-center gap-2">
                                                    <button type="button"
                                                        class="btn btn-md btn-outline-primary-stepper w-100 px-2 py-2"
                                                        (click)="stepper.previous()">
                                                        <i class="fa-solid fa-arrow-left me-2"
                                                            aria-hidden="true"></i>Previous
                                                    </button>
                                                    <button type="button"
                                                        class="btn btn-md btn-primary-stepper w-100 px-0 py-0"
                                                        (click)="stepper.next()" [disabled]="attachmentsForm.invalid">
                                                        Next<i class="fa-solid fa-arrow-right ms-2"
                                                            aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                    </mdb-step>
                                    <!-- STEP 6: Contact Info -->
                                    <mdb-step [stepForm]="contactInfoForm" [name]="'Contact Info'"
                                        [editable]="!isSubmitted()">
                                        <form [formGroup]="contactInfoForm" class="mt-3">
                                            <h4 class="mb-3">How can we reach you?</h4>
                                            <p class="mb-4">
                                                We'll use this information to contact you about your request.
                                            </p>

                                            <!-- Preferred Contact Method -->
                                            <div class="row mb-3">
                                                <div class="col-12 col-md-2">
                                                    <mdb-form-control>
                                                        <mdb-select formControlName="preferredContact"
                                                            aria-labelledby="preferredContactLabel"
                                                            aria-label="Preferred Contact, required"
                                                            aria-required="true"
                                                            [attr.aria-invalid]="preferredContact.invalid && (preferredContact.dirty || preferredContact.touched)">
                                                            @for (method of availableContactMethods(); track
                                                            method.value) {
                                                            <mdb-option [value]="method.value">{{ method.label
                                                                }}</mdb-option>
                                                            }
                                                        </mdb-select>
                                                        <label mdbLabel class="form-label" id="preferredContactLabel">
                                                            Preferred Contact <span class="form-required-asterisk"
                                                                aria-hidden="true">*</span>
                                                        </label>
                                                        @if (preferredContact.invalid && (preferredContact.dirty ||
                                                        preferredContact.touched)) {
                                                        <mdb-error class="mt-n1">Please select a contact
                                                            method.</mdb-error>
                                                        }
                                                        @if (preferredContact.valid) {
                                                        <mdb-success></mdb-success>
                                                        }
                                                    </mdb-form-control>
                                                </div>

                                                @if (availableContactMethods().length > 0) {
                                                <div class="col-12 col-md-9 fw-bold align-self-center">
                                                    <span>{{ getContactDisplay() }}</span>
                                                </div>
                                                } @else {
                                                <div class="col-12 col-md-9">
                                                    <div class="alert alert-warning mb-0" role="alert">
                                                        <i class="fa-solid fa-circle-exclamation me-2"
                                                            aria-hidden="true"></i>
                                                        No contact information on file.
                                                    </div>
                                                </div>
                                                }
                                            </div>

                                            <!-- Language Preference -->
                                            <div class="row mb-4">
                                                <div class="col-12 col-md-2 col-lg-2">
                                                    <mdb-form-control>
                                                        <mdb-select formControlName="language"
                                                            aria-labelledby="languageLabel">
                                                            <mdb-option [value]="'English'">English</mdb-option>
                                                            <mdb-option [value]="'Spanish'">Spanish</mdb-option>
                                                            <mdb-option [value]="'Other'">Other</mdb-option>
                                                        </mdb-select>
                                                        <label mdbLabel class="form-label" id="languageLabel">Preferred
                                                            Language</label>
                                                    </mdb-form-control>
                                                </div>
                                            </div>

                                            <h6 class="mb-3 text-muted fst-italic">&hyphen; Demographic Information &hyphen;</h6>
                                            <p class="mb-4">
                                       
                                            </p>
                                            <!-- Zipcode -->
                                            <div class="row mb-4">
                                                <div class="col-12 col-md-2 col-lg-2">
                                                    <mdb-form-control>
                                                        <input mdbValidate mdbInput type="text" id="zipcode"
                                                            formControlName="zipcode" class="form-control"
                                                            [maxlength]="5" />
                                                        <label mdbLabel class="form-label" for="zipcode">
                                                            Zip Code <span class="form-required-asterisk"
                                                                aria-hidden="true">*</span>
                                                        </label>
                                                        @if (zipcode.invalid && (zipcode.dirty || zipcode.touched)) {
                                                        <mdb-error class="mt-n1">Please enter a valid 5-digit zip
                                                            code.</mdb-error>
                                                        }
                                                        @if (zipcode.valid) {
                                                        <mdb-success></mdb-success>
                                                        }
                                                    </mdb-form-control>
                                                </div>
                                            </div>

                                             <div class="row mb-4">
                                                <div class="col-12 col-md-3 col-lg-3">
                                                    <mdb-form-control>
                                                        <mdb-select formControlName="visitedBefore"
                                                            aria-labelledby="VisitedBeforeLabel">
                                                            <mdb-option [value]="'Yes - Same Case/Issue'">Yes - Same Case/Issue</mdb-option>
                                                            <mdb-option [value]="'Yes - Different Case/Issue'">Yes - Different Case/Issue</mdb-option>
                                                            <mdb-option [value]="'Yes - I don\'t remember the case'">Yes - I don't remember the case</mdb-option>
                                                            <mdb-option [value]="'No'">No</mdb-option>
                                                        </mdb-select>
                                                        <label mdbLabel class="form-label" id="VisitedBeforeLabel">Have you visited Self-Help before?</label>
                                                    </mdb-form-control>
                                                </div>
                                            </div>


                                        </form>

                                        <!-- Navigation Buttons -->
                                        @if(!isMobile())
                                        {
                                        <div class="row pt-3">
                                            <div class="col-12 col-md-4 col-lg-4">
                                                <div class="d-flex justify-content-center gap-2">
                                                    <button
                                                        class="btn btn-md btn-outline-primary-stepper w-100 px-2 py-2"
                                                        (click)="stepper.previous()">
                                                        <i class="fa-solid fa-arrow-left me-2"
                                                            aria-hidden="true"></i>Previous
                                                    </button>
                                                    <button class="btn btn-md btn-primary-stepper w-100 px-2 py-2"
                                                        (click)="nextStep(stepper, contactInfoForm)">
                                                        Next<i class="fa-solid fa-arrow-right ms-2"
                                                            aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                    </mdb-step>

                                    <!-- STEP 7: Review -->
                                    <mdb-step [name]="'Review'" [stepForm]="attestReviewSubmitForm"
                                        [editable]="!isSubmitted()">
                                        <div class="mt-3">
                                            <h4 class="mb-3">Review your request</h4>
                                            <p class="mb-4">
                                                Please review your information before submitting. You can go back to
                                                make changes if needed.
                                            </p>

                                            <div class="row">
                                                <div class="col-12 col-md-6 col-lg-8">
                                                    <div class="card">
                                                        <div class="card-body">
                                                            <h5 class="card-title mb-4">Summary</h5>
                                                            <!-- Full Name -->
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Name:</div>
                                                                <div class="col-12 col-md-8">
                                                                    {{getFullName()}}</div>
                                                            </div>
                                                            <!-- Case Type -->
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Case Type:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getCaseTypeDisplay(getCompleteFormData().caseType)
                                                                    }}</div>
                                                            </div>

                                                            <!-- Related Case -->
                                                            @if (getCompleteFormData().relatedCase) {
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Related Case:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getCompleteFormData().relatedCase }}</div>
                                                            </div>
                                                            }

                                                            <hr>

                                                            <!-- Service Category -->
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Service Type:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getCategoryDisplay(getCompleteFormData().category)
                                                                    }}</div>
                                                            </div>

                                                            <!-- Document Preparation Fields -->
                                                            @if (getCompleteFormData().docPrepFormType) {
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Form Type:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getFormTypeDisplay(getCompleteFormData().docPrepFormType)
                                                                    }}
                                                                </div>
                                                            </div>
                                                            }

                                                            <!-- Form Review Fields -->
                                                            @if (getCompleteFormData().formReviewType) {
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Form Type:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getFormTypeDisplay(getCompleteFormData().formReviewType)
                                                                    }}
                                                                </div>
                                                            </div>
                                                            }

                                                            <!-- Description -->
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Description:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getCompleteFormData().description }}</div>
                                                            </div>

                                                            <hr>

                                                            <!-- Contact Info -->
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Contact Method:
                                                                </div>
                                                                <div class="col-12 col-md-8">
                                                                    {{
                                                                    getContactMethodDisplay(getCompleteFormData().preferredContact)
                                                                    }}
                                                                    - {{ getCompleteFormData().contactDisplay }}
                                                                </div>
                                                            </div>

                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Language:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getCompleteFormData().language |
                                                                    titlecase }}</div>
                                                            </div>

                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Zip Code:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getCompleteFormData().zipcode }}
                                                                </div>
                                                            </div>

                                                            
                                                            <div class="row mb-3">
                                                                <div class="col-12 col-md-4 fw-bold">Visited Before:</div>
                                                                <div class="col-12 col-md-8">{{
                                                                    getCompleteFormData().visitedBefore |
                                                                    titlecase }}</div>
                                                            </div>

                                                            @if (uploadedFiles().length > 0) {
                                                            <hr>
                                                            <div class="uploaded-files-list mt-4" role="region"
                                                                aria-labelledby="uploaded-files-heading">
                                                                <h6 class="mb-3 fw-bold" id="uploaded-files-heading">
                                                                    Uploaded Files ({{ uploadedFiles().length }})
                                                                </h6>

                                                                <!-- File List -->
                                                                <div class="row">
                                                                    <div class="col-12 col-md-12">
                                                                        <ul class="list-group"
                                                                            aria-labelledby="uploaded-files-heading">
                                                                            @for (file of uploadedFiles(); track
                                                                            file.name + file.size)
                                                                            {
                                                                            <li class="list-group-item py-2">
                                                                                <div
                                                                                    class="d-flex align-items-center gap-2">
                                                                                    <!-- Icon (hidden on mobile) -->
                                                                                    <div class="file-icon d-none d-md-flex"
                                                                                        aria-hidden="true">
                                                                                        <i
                                                                                            class="fas fa-file text-muted fs-4"></i>
                                                                                    </div>

                                                                                    <!-- Filename - takes all remaining left space -->
                                                                                    <span
                                                                                        class="fw-semibold text-truncate flex-grow-1 min-w-0"
                                                                                        [title]="file.name">
                                                                                        {{ file.name }}
                                                                                    </span>

                                                                                    <!-- Right side: type, size, remove - all pinned right -->
                                                                                    <div
                                                                                        class="d-flex align-items-center gap-2 flex-shrink-0">
                                                                                        <span
                                                                                            class="badge file-badge">{{
                                                                                            getFileExtension(file)
                                                                                            }}</span>
                                                                                        <span
                                                                                            class="small text-muted text-nowrap">{{
                                                                                            formatFileSize(file.size)
                                                                                            }}</span>
                                                                                    </div>
                                                                                </div>
                                                                            </li>
                                                                            }
                                                                        </ul>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                            }

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="row mt-3">
                                                <div class="col-12 col-md-8">
                                                    <span class="fw-bold">Before submitting this service request, I
                                                        confirm that:</span>
                                                </div>
                                            </div>
                                            <form [formGroup]="attestReviewSubmitForm" class="mt-3">
                                                <div class="row">
                                                    <div class="col-12 col-md-8 col-lg-8">
                                                        <div class="form-check mb-2">
                                                            <input class="form-check-input" type="checkbox"
                                                                id="attestNoAttorneyCheck"
                                                                formControlName="attestNoAttorneyCheck"
                                                                aria-required="true"
                                                                [attr.aria-describedby]="attestNoAttorneyCheck.hasError('required') && (attestNoAttorneyCheck.dirty || attestNoAttorneyCheck.touched) ? 'checkbox-errors' : null"
                                                                required>
                                                            <label for="attestNoAttorneyCheck" class="form-check-label">
                                                                <span class="form-required-asterisk"
                                                                    aria-hidden="true">*</span>
                                                                <span class="visually-hidden">(required)</span>
                                                                I am not represented by an attorney in this matter.
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-md-8 col-lg-8">
                                                        <div class="form-check mb-2 fs-6">
                                                            <input class="form-check-input" type="checkbox"
                                                                id="attestFormsCompleteCheck"
                                                                formControlName="attestFormsCompleteCheck"
                                                                aria-required="true"
                                                                [attr.aria-describedby]="attestFormsCompleteCheck.hasError('required') && (attestFormsCompleteCheck.dirty || attestFormsCompleteCheck.touched) ? 'checkbox-errors' : null"
                                                                required>
                                                            <label for="attestFormsCompleteCheck"
                                                                class="form-check-label">
                                                                <span class="form-required-asterisk"
                                                                    aria-hidden="true">*</span>
                                                                <span class="visually-hidden">(required)</span>
                                                                All forms I am submitting are fully completed and
                                                                signed, as
                                                                required.
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-md-8 col-lg-8">
                                                        <div class="form-check mb-2">
                                                            <input class="form-check-input" type="checkbox"
                                                                id="attestTrueAndAccurateFormCheck"
                                                                formControlName="attestTrueAndAccurateFormCheck"
                                                                aria-required="true"
                                                                [attr.aria-describedby]="attestTrueAndAccurateFormCheck.hasError('required') && (attestTrueAndAccurateFormCheck.dirty || attestTrueAndAccurateFormCheck.touched) ? 'checkbox-errors' : null"
                                                                required>
                                                            <label for="attestTrueAndAccurateFormCheck"
                                                                class="form-check-label">
                                                                <span class="form-required-asterisk"
                                                                    aria-hidden="true">*</span>
                                                                <span class="visually-hidden">(required)</span>
                                                                All information and documents I am submitting are true
                                                                and
                                                                accurate to the best of my knowledge.
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div class="col-12 col-md-8 col-lg-8">
                                                        <div class="form-check mb-2">
                                                            <input class="form-check-input" type="checkbox"
                                                                id="attestComplySelfHelpCheck"
                                                                formControlName="attestComplySelfHelpCheck"
                                                                aria-required="true"
                                                                [attr.aria-describedby]="attestComplySelfHelpCheck.hasError('required') && (attestComplySelfHelpCheck.dirty || attestComplySelfHelpCheck.touched) ? 'checkbox-errors' : null"
                                                                required>
                                                            <label for="attestComplySelfHelpCheck"
                                                                class="form-check-label">
                                                                <span class="form-required-asterisk"
                                                                    aria-hidden="true">*</span>
                                                                <span class="visually-hidden">(required)</span>
                                                                I understand that Self-Help Services is NOT YOUR LAWYER,
                                                                but is a neutral person who does not represent any
                                                                party. There is no attorney-client relationship between
                                                                you and the Self-Help Services. What you tell Self-Help
                                                                Services staff is not confidential. You should consult
                                                                your own attorney if you want personalized advice or
                                                                strategy, to have a confidential conversation, or to be
                                                                represented by an attorney in court.
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </form>
                                            <!-- Navigation Buttons -->
                                            @if(!isMobile())
                                            {
                                            <div class="row pt-3">
                                                <div class="col-12 col-md-4 col-lg-4">
                                                    <div class="d-flex justify-content-center gap-2">
                                                        <button
                                                            class="btn btn-md btn-outline-primary-stepper w-100 px-2 py-2"
                                                            (click)="stepper.previous()">
                                                            <i class="fa-solid fa-arrow-left me-2"
                                                                aria-hidden="true"></i>Previous
                                                        </button>
                                                        <button class="btn btn-md btn-success w-100 px-0 py-0"
                                                            (click)="submitServiceRequest()"
                                                            [disabled]="isSubmitted() || isSubmitting() || !canSubmitServiceRequest() || missingAttachmentConfirmation()">
                                                            @if (isSubmitting()) {
                                                            <span class="spinner-border spinner-border-sm me-2"
                                                                role="status" aria-hidden="true"></span>
                                                            Submitting...
                                                            } @else {
                                                            <i class="fa-solid fa-paper-plane me-2"
                                                                aria-hidden="true"></i>Submit
                                                            Request
                                                            }
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            }
                                            @else{
                                            <div class="d-grid gap-2 mt-4">
                                                <button type="button" class="btn btn-success bg-success-dark btn-lg"
                                                    (click)="submitServiceRequest()"
                                                    [disabled]="isSubmitted() || isSubmitting() || !canSubmitServiceRequest() || missingAttachmentConfirmation()">
                                                    @if (isSubmitting()) {
                                                    <span class="spinner-border spinner-border-sm me-2" role="status"
                                                        aria-hidden="true"></span>
                                                    Submitting...
                                                    } @else {
                                                    <i class="fa-solid fa-paper-plane me-2"
                                                        aria-hidden="true"></i>Submit Request
                                                    }
                                                </button>
                                            </div>
                                            }

                                            @if (missingAttachmentConfirmation()) {
                                            <div class="alert alert-warning mt-4" role="alert" aria-live="assertive">
                                                <p>You have not attached any documents for Form Review. Would you like
                                                    to submit without an attachment or attach a document now?</p>
                                                <div class="d-flex flex-wrap gap-2">
                                                    <button type="button" class="btn btn-outline-primary"
                                                        (click)="returnToAttachments()">
                                                        Attach a document
                                                    </button>
                                                    <button type="button" class="btn btn-success"
                                                        (click)="submitServiceRequest(true)"
                                                        [disabled]="isSubmitted() || isSubmitting()">
                                                        Submit without attachment
                                                    </button>
                                                </div>
                                            </div>
                                            }

                                            @if (submissionError()) {
                                            <div class="alert alert-danger mt-4" role="alert" aria-live="assertive">
                                                <i class="fa-solid fa-circle-exclamation me-2" aria-hidden="true"></i>
                                                {{ submissionError() }}
                                            </div>
                                            }

                                        </div>
                                    </mdb-step>

                                    <!-- STEP 8: Confirmation -->
                                    <mdb-step [name]="'Confirmation'">
                                        <div class="row">
                                            <div class="col-12 col-md-6 col-lg-8 confirmation-card">
                                                <div class="mt-4 text-center">
                                                    <div class="mb-4">
                                                        <i class="fa-solid fa-circle-check text-success"
                                                            aria-hidden="true" style="font-size: 5rem;"></i>
                                                    </div>

                                                    <h2 class="mb-3">Your request has been submitted!</h2>

                                                    <p class="mb-4">
                                                        We've received your service request and will get back to you
                                                        soon.
                                                    </p>
                                                    <div class="row align-items-center">
                                                        <div class="col-12 text-center">
                                                            <span class="fs-4">Reference Code: {{ referenceCode()
                                                                }}</span>
                                                        </div>
                                                        <div class="col-12 col-md-6 mx-auto">
                                                            <app-gradientsegmentbar></app-gradientsegmentbar>
                                                        </div>
                                                    </div>


                                                    <p class="text-muted mt-3 mb-4">
                                                        Save this reference code for your records. You can use it to
                                                        track your
                                                        request.
                                                    </p>

                                                    <div class="d-grid gap-2 col-md-6 col-lg-8 mx-auto mt-4">
                                                        <button type="button" class="btn btn-primary btn-lg"
                                                            (click)="viewServiceRequests()">
                                                            <i class="fa-solid fa-list me-2" aria-hidden="true"></i>View
                                                            My Service
                                                            Requests
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </mdb-step>
                                </mdb-stepper>
                            </div>



                        </div>
                    </div>
                </div>

                <app-gradientsegmentbar></app-gradientsegmentbar>
            </div>
            <div class="col-xxl-1"></div>
        </div>
    </div>
</div>
